import React, { createContext, useState, useEffect, useMemo } from "react";
import { EVENT_CAPABILITIES } from "../constants/permissions";
import { useUserContext } from "../hooks/useUserContext";
import {
  addFeatureFlagsStorage,
  FLAGS_WITHOUT_EARLY_ACCESS,
  getDefaultFeatureFlags,
  getFeatureFlagsStorage
} from "../utils/localStorage/featureFlagsStorage";

type FeatureFlagContextType = {
  featureFlags: FeatureFlag[];
  setFeatureFlags: CallableFunction;
  toggleFlag: CallableFunction;
  isFlagEnabled: (status: string) => boolean;
  /** True once the stored feature flags have been read and the user has loaded (so gated flags are stable before consumers read them). */
  featureFlagsLoaded: boolean;
};

type UserContextProps = {
  children: React.ReactNode;
};

export type FeatureFlag = {
  key: string;
  title: string;
  description?: string;
  isEnabled: boolean;
};

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: [],
  setFeatureFlags: () => true,
  toggleFlag: () => true,
  isFlagEnabled: () => true,
  featureFlagsLoaded: true
});

export const FeatureFlagContextProvider = ({ children }: UserContextProps) => {
  const { user } = useUserContext();
  const hasEarlyAccess = !!user?.capabilities?.includes(
    EVENT_CAPABILITIES.HAS_EARLY_ACCESS_PREVIEW
  );

  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [storedFlagsLoaded, setStoredFlagsLoaded] = useState(false);

  /**
   * Set feature flags on load
   */
  useEffect(() => {
    (async () => {
      const storedFeatureFlags = await getFeatureFlagsStorage();

      const initialFeatureFlags = storedFeatureFlags.map((featureFlag: any) => {
        const storedVersion =
          storedFeatureFlags &&
          storedFeatureFlags.find((flag: any) => featureFlag.key === flag.key);
        return storedVersion
          ? { ...featureFlag, isEnabled: storedVersion.isEnabled }
          : featureFlag;
      });

      setFeatureFlags(initialFeatureFlags);
      setStoredFlagsLoaded(true);
    })();
  }, []);

  /**
   * Update storage when they change
   */
  useEffect(() => {
    addFeatureFlagsStorage(featureFlags);
  }, [featureFlags]);

  /**
   * Set a new value of a flag
   *
   * @param flagKey
   * @param newIsEnabled
   */
  const toggleFlag = (flagKey: string, newIsEnabled: boolean) => {
    if (!hasEarlyAccess && !FLAGS_WITHOUT_EARLY_ACCESS.includes(flagKey)) {
      return;
    }
    setFeatureFlags(
      featureFlags.map((featureFlag: FeatureFlag) =>
        featureFlag.key === flagKey
          ? { ...featureFlag, isEnabled: newIsEnabled }
          : featureFlag
      )
    );
  };

  /**
   * Flags as consumers see them: without early access every flag falls back to
   * its default except those switched outside Alpha Features
   * (FLAGS_WITHOUT_EARLY_ACCESS). The raw stored values are kept untouched so
   * an early-access user's choices survive on a shared device.
   */
  const effectiveFlags = useMemo(() => {
    if (hasEarlyAccess) {
      return featureFlags;
    }
    const defaults = getDefaultFeatureFlags();
    return featureFlags.map((featureFlag) =>
      FLAGS_WITHOUT_EARLY_ACCESS.includes(featureFlag.key)
        ? featureFlag
        : {
            ...featureFlag,
            isEnabled:
              defaults.find((flag) => flag.key === featureFlag.key)
                ?.isEnabled ?? false
          }
    );
  }, [featureFlags, hasEarlyAccess]);

  const featureFlagsLoaded = storedFlagsLoaded && user !== undefined;

  /**
   * Check if a flag is enabled
   */
  const isFlagEnabled = (flagKey: string) =>
    !!effectiveFlags.find(
      (featureFlag: FeatureFlag) =>
        featureFlag.key === flagKey && featureFlag.isEnabled
    );

  const contextValue = useMemo(
    () => ({
      featureFlags: effectiveFlags,
      setFeatureFlags,
      toggleFlag,
      isFlagEnabled,
      featureFlagsLoaded
    }),
    [effectiveFlags, featureFlagsLoaded]
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to use the FeatureFlag context
 */
export const useFeatureFlagContext = () => {
  const context = React.useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagContextProvider"
    );
  }
  return context;
};
